import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { RichTextField } from '@/components/editor/RichTextField';
import { PublishingSidebar, type PublishStatus } from '@/components/editor/PublishingSidebar';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { useResourceRecord, useSaveResource } from '@/lib/api/resource';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { useUnsavedChanges } from '@/lib/forms/useUnsavedChanges';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useForm } from 'react-hook-form';

import type { VocationalProgramInput, VocationalProgramRow } from './types';

/**
 * docs/01-INFORMATION-ARCHITECTURE.md §13.3 — a vocational programme.
 *
 * The first full-page editor, and the first screen to use all of §4: §4.1's
 * translatable fields, §4.2's rich text, §4.3's slug with its redirect warning,
 * §4.4's publishing sidebar, and §1's unsaved-changes guard.
 *
 * §1.4's accent is a *stored key*, never derived from a row's position — the
 * seeder's comment says the same. A grid that coloured cards by index would
 * recolour every one of them the moment somebody reordered the list.
 */
const ACCENTS = ['teal', 'violet', 'amber', 'navy', 'rose', 'sky', 'lime', 'slate'] as const;

interface Fields {
  name: TranslatedValue;
  slug: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  admission_requirements: TranslatedValue;
  career_outcomes: TranslatedValue;
  qualification: TranslatedValue;
  accent: string;
  icon: string;
  duration_months: string;
  credits: string;
  is_accredited: boolean;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
}

export interface VocationalProgramEditorProps {
  /** The record id, or `new`. Comes from the module's splat route. */
  recordId: string;
}

export function VocationalProgramEditor({ recordId }: VocationalProgramEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNew = recordId === 'new';

  const [formError, setFormError] = useState<string | null>(null);

  const record = useResourceRecord<VocationalProgramRow>(
    'vocational-programs',
    isNew ? null : Number(recordId),
  );

  const save = useSaveResource<VocationalProgramRow, VocationalProgramInput>('vocational-programs');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Fields>({ defaultValues: emptyProgram() });

  // The record arrives after the first render, so the form is filled rather
  // than initialised — `reset` also clears the dirty flag, which the guard
  // below depends on.
  useEffect(() => {
    if (record.data) {
      reset(toFields(record.data.data));
    }
  }, [record.data, reset]);

  const guard = useUnsavedChanges(isDirty && !isSubmitting);

  const values = watch();

  const onSubmit = handleSubmit(async (form) => {
    setFormError(null);

    try {
      const saved = await save.mutateAsync({
        id: isNew ? undefined : Number(recordId),
        input: toInput(form),
      });

      // Straight to the saved record, so a create lands on its own address
      // rather than leaving the form thinking it is still new.
      navigate(`/vocational/programs/${saved.data.id}`, { replace: true });
      reset(toFields(saved.data));
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'name',
        'slug',
        'summary',
        'body',
        'accent',
        'icon',
        'duration_months',
        'credits',
        'sort_order',
        'status',
        'published_at',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (!isNew && record.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {isNew ? t('vocational.programs.new') : t('vocational.programs.edit')}
          </h1>

          {record.data?.data.pending_review ? (
            <Chip tone="gold" className="mt-2">
              {t('editor.publishing.awaitingReview')}
            </Chip>
          ) : null}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vocational/programs')}
        >
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('vocational.programs.fields.name')}
            name="name"
            value={values.name}
            onChange={(next) => setValue('name', next, { shouldDirty: true })}
            error={errors.name?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record.data?.data.slug}
            published={values.status === 'published'}
            error={errors.slug?.message}
          />

          <TranslatableField
            label={t('vocational.programs.fields.summary')}
            hint={t('vocational.programs.fields.summaryHint')}
            name="summary"
            multiline
            rows={3}
            value={values.summary}
            onChange={(next) => setValue('summary', next, { shouldDirty: true })}
            required={false}
          />

          <RichTextField
            label={t('vocational.programs.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
            error={errors.body?.message}
          />

          <RichTextField
            label={t('vocational.programs.fields.admission')}
            hint={t('vocational.programs.fields.admissionHint')}
            value={values.admission_requirements}
            onChange={(next) => setValue('admission_requirements', next, { shouldDirty: true })}
          />

          <RichTextField
            label={t('vocational.programs.fields.outcomes')}
            value={values.career_outcomes}
            onChange={(next) => setValue('career_outcomes', next, { shouldDirty: true })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('vocational.programs.fields.accent')}
              hint={t('vocational.programs.fields.accentHint')}
              {...register('accent')}
              options={ACCENTS.map((accent) => ({
                value: accent,
                label: t(`vocational.programs.accents.${accent}`),
              }))}
            />

            <Input
              {...register('icon')}
              label={t('vocational.programs.fields.icon')}
              hint={t('vocational.programs.fields.iconHint')}
              error={errors.icon?.message}
            />

            <Input
              {...register('duration_months')}
              type="number"
              min={1}
              label={t('vocational.programs.fields.duration')}
              error={errors.duration_months?.message}
            />

            <Input
              {...register('credits')}
              type="number"
              min={1}
              label={t('vocational.programs.fields.credits')}
              error={errors.credits?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('vocational.programs.fields.order')}
              hint={t('vocational.programs.fields.orderHint')}
            />

            <label className="flex items-center gap-2 self-end pb-3 text-body-sm text-primary">
              <input
                type="checkbox"
                {...register('is_accredited')}
                className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t('vocational.programs.fields.accredited')}
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="vocational"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record.data?.data.updated_at}
            updatedBy={record.data?.data.updated_by}
            pendingReview={record.data?.data.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              name: { label: t('vocational.programs.fields.name'), value: values.name },
              summary: { label: t('vocational.programs.fields.summary'), value: values.summary },
              body: { label: t('vocational.programs.fields.body'), value: values.body },
            }}
          />
        </div>
      </div>

      {/* §1 — the router half of the unsaved-changes guard. `beforeunload`
          covers the tab closing; this covers the sidebar. */}
      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function emptyProgram(): Fields {
  return {
    name: {},
    slug: {},
    summary: {},
    body: {},
    admission_requirements: {},
    career_outcomes: {},
    qualification: {},
    accent: 'rose',
    icon: '',
    duration_months: '',
    credits: '',
    is_accredited: false,
    sort_order: 0,
    status: 'draft',
    published_at: null,
  };
}

function toFields(row: VocationalProgramRow): Fields {
  return {
    name: row.name,
    slug: row.slug,
    summary: row.summary,
    body: row.body,
    admission_requirements: row.admission_requirements,
    career_outcomes: row.career_outcomes,
    qualification: row.qualification,
    accent: row.accent,
    icon: row.icon ?? '',
    // The numeric fields are strings in the form so an emptied input is `''`
    // rather than `NaN`, which is what a `valueAsNumber` register produces and
    // what the server then rejects as "must be an integer".
    duration_months: row.duration_months === null ? '' : String(row.duration_months),
    credits: row.credits === null ? '' : String(row.credits),
    is_accredited: row.is_accredited,
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
  };
}

function toInput(form: Fields): VocationalProgramInput {
  return {
    ...form,
    icon: form.icon.trim() === '' ? null : form.icon.trim(),
    duration_months: form.duration_months === '' ? null : Number(form.duration_months),
    credits: form.credits === '' ? null : Number(form.credits),
    sort_order: Number(form.sort_order),
  };
}
